import { useMemo, useState } from 'react'
import { useSearchParams } from 'react-router'
import { useAuth } from '../hooks/useAuth'
import {
  getQueryValue,
  normalizeEmailLikeValue,
  normalizeVerificationCode,
} from '../utils/authFlowParams'
import { validatePassword } from '../lib/validatePassword'
import { AuthShell } from '../components/auth/AuthShell'
import {
  AuthFormCard,
  AuthSubmitButton,
  AuthFooterLink,
} from '../components/auth/AuthFormCard'
import { PasswordInput } from '../components/common'
import { PasswordRequirementsField } from '../components/auth/PasswordRequirementsField'

function AcceptInvitationPage() {
  const { acceptInvitation } = useAuth()
  const [searchParams] = useSearchParams()
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [submitted, setSubmitted] = useState(false)

  const userId = useMemo(
    () => getQueryValue(searchParams, ['user_id', 'userId', 'userid']),
    [searchParams]
  )
  const code = useMemo(
    () => normalizeVerificationCode(getQueryValue(searchParams, ['code', 'invite_code', 'inviteCode'])),
    [searchParams]
  )
  const inviteeEmail = useMemo(
    () => normalizeEmailLikeValue(getQueryValue(searchParams, ['email', 'login_name', 'loginName'])),
    [searchParams]
  )

  const handleSubmit = async (event) => {
    event.preventDefault()
    setError('')

    const validationError = validatePassword(password, confirmPassword)
    if (validationError) {
      setError(validationError)
      return
    }
    if (!userId || !code) {
      setError('The invitation link is missing required verification details')
      return
    }

    setLoading(true)
    try {
      await acceptInvitation(userId, code, password)
      setSubmitted(true)
    } catch (err) {
      setError(err.message || 'Failed to accept invitation')
    } finally {
      setLoading(false)
    }
  }

  return (
    <AuthShell>
      <AuthFormCard
        title="Accept invitation"
        subtitle="Set your password to activate your Studio access."
        helperText={inviteeEmail ? `Invited as ${inviteeEmail}` : undefined}
        onSubmit={handleSubmit}
        errorMessage={error}
        successMessage={
          submitted ? 'Invitation accepted. Sign in with your new password.' : ''
        }
        footer={<AuthFooterLink to="/login">Back to login</AuthFooterLink>}
      >
        <PasswordRequirementsField
          id="invite_password"
          label="Password"
          value={password}
          onChange={setPassword}
          placeholder="Create a strong password"
          required
          disabled={loading || submitted}
          autoFocus
          autoComplete="new-password"
        />

        <PasswordInput
          id="invite_confirm_password"
          label="Confirm Password"
          value={confirmPassword}
          onChange={setConfirmPassword}
          placeholder="Re-enter your password"
          required
          disabled={loading || submitted}
          autoComplete="new-password"
          size="large"
        />

        <AuthSubmitButton
          loading={loading}
          submitted={submitted}
          loadingLabel="Activating..."
        >
          Activate account
        </AuthSubmitButton>
      </AuthFormCard>
    </AuthShell>
  )
}

export default AcceptInvitationPage
